<template>
  <v-form ref="formValid" v-model="valid">
    <v-dialog v-model="dialog" width="800" dark>
      <template v-slot:activator="{ on, attrs }">
        <v-btn
          :id="`add-dat-version-${datType}`"
          class="version-btn mx-6 my-2"
          dark
          outlined
          rounded
          v-bind="attrs"
          v-on="on"
        >
          {{ $t(`map.tolldomains.dats.add_version`) }}
        </v-btn>
      </template>
      <v-card
        class="dat-container"
        @drop.prevent="onDrop($event)"
        @dragover.prevent="dragover = true"
        @dragenter.prevent="dragover = true"
        @dragleave.prevent="dragover = false"
      >
        <v-card-title class="title pa-4 pl-11">{{ $t('map.tolldomains.dats.add_dat_version_modal_title', { type: datType }) }}</v-card-title>
        <v-divider />
        <div class="ml-6 mb-1 mt-4 file-name required">{{ $t('map.tolldomains.dats.list.header_valid_from_date') }}</div>
        <DatePicker
          id="add-dat-version-valid-from-datepicker"
          v-model="validFrom"
          :showTime="{ format: 'HH:mm' }"
          :disabledDate="disabledDate"
          :disabledTime="disabledTime"
          class="version-date-field ml-6"
          placeholder="DD/MM/YYYY HH:mm"
          format="DD/MM/YYYY HH:mm"
        />
        <v-stepper v-model="currentStepIndex">
          <v-stepper-header class="mb-7" dark>
            <template v-for="(title, index) in steps">
              <v-stepper-step
                :key="`step-${index}`"
                :step="index + 1"
              >
                <span>{{ $t(title) }} </span>
              </v-stepper-step>
              <v-divider v-if="index + 1 !== steps.length" :key="index + 1" />
            </template>
          </v-stepper-header>
          <v-stepper-items>
            <v-stepper-content
              v-for="(title, index) in steps"
              :key="`content-step-${index}`"
              :step="index + 1"
              class="pa-0"
            >
              <AddDatVersionDialog
                v-if="index === 0"
                :key="keyObu "
                :index="index"
                :datId="datId"
                :datType="datType"
                :masterAccount="masterAccount"
                :datnames="datnames"
                :datVersionIds="datVersionIds"
                @saveDatVersion="saveDatVersion($event)"
              />

              <AddDatVersionDialog
                v-if="index === 1"
                :key="keyObu "
                :index="index"
                :datId="datId"
                :datType="datType"
                :masterAccount="masterAccount"
                :datnames="datnames"
                :datVersionIds="datVersionIds"
                @saveDatVersion="saveDatVersion($event)"
              />
            </v-stepper-content>
          </v-stepper-items>
        </v-stepper>
        <v-card-text class="pa-4 pl-11 add-file-actions">
          <v-btn
            v-if="currentStepIndex === 1"
            id="add-dat-version-cancel"
            class="cancel"
            dark
            rounded
            @click="close"
          >
            {{ $t('common.cancel') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 2"
            id="add-dat-version-back"
            class="back"
            dark
            rounded
            @click="goBack"
          >
            {{ $t('common.back') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 1"
            id="add-dat-version-continue"
            class="ctp-btn my-0"
            dark
            :disabled="!obuFormValid || !validFrom"
            :color="colors.secondary"
            @click="goNext"
          >
            {{ $t('common.continue') }}
          </v-btn>
          <v-btn
            v-if="currentStepIndex === 2"
            id="add-dat-version-confirm"
            class="ctp-btn my-0"
            dark
            :disabled="!dtcoFormValid || !validFrom"
            :color="colors.secondary"
            @click="confirm"
          >
            {{ $t('common.confirm') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-form>
</template>

<script lang="ts">
import { Component, Mixins, Prop } from 'vue-property-decorator';
import { colors } from '@/constants/theme/colors';
import Validation from '@/mixins/Validation';
import { DatePicker } from 'ant-design-vue';
import moment, { Moment } from 'moment';
import { max, min, range } from 'lodash';
import { store as userPermissionStore } from '@/store/modules/permissions/types';
import { DatRelatedV3 } from 'ctp-toll-domain';
import { store as datsStore } from '@/store/modules/dats/types';
import AddDatVersionDialog from './AddDatVersionDialog.vue';

@Component({
  components: {
    DatePicker,
    AddDatVersionDialog
  }
})
export default class AddDatVersionDialogStepper extends Mixins(Validation) {
    @Prop()
    private datType!: string;

    @Prop()
    private datnames!: string[];

    @Prop()
    private datId!: number[];

    @Prop()
    private datVersionIds!: number[];

    @Prop()
    private masterAccount!: string;

    @userPermissionStore.Getter
    private getCurrentUserIsAbleTo: (readOrWrite: string, featureName: string) => boolean;

    @datsStore.Getter
    private getDatStatus!: (dat: DatRelatedV3) => any;

    private datuidRules = [
      this.requiredRule('common.error.forms.inputs.validator.mandatory')
    ];

    private datfileRules = [
      this.maxFileSize(1032, 'common.error.forms.inputs.validator.datfile_too_large')
    ];

    private currentStepIndex = 1;

    private steps = [
      'OBU5+',
      'DTCO'
    ];

    private async navigate(index: number) {
      this.currentStepIndex = min([index + 1, this.steps.length])!;
    }

    private async goNext() {
      this.currentStepIndex = min([this.currentStepIndex + 1, this.steps.length])!;
    }

    private async goBack() {
      this.currentStepIndex = max([this.currentStepIndex - 1, 1])!;
    }

    private keyObu = false;

    private keyDtco = false;

    private colors = colors;

    private dialog = false;

    private uploaded: File|null = null;

    private obuFormValid = false;

    private dtcoFormValid = false;

    private valid = false;

    private validFrom: Moment|null = null;

    private fileData: any = {
      filenameObu: '',
      filenameDtco: '',
      uid: null,
      signatureObu: '',
      signatureDtco: '',
      fileDataObu: null,
      fileDataDtco: null
    };

    private onDrop(e: any) {
      if (e.dataTransfer.files.length === 1) {
        this.uploaded = e.dataTransfer.files[0];
        this.onFileChange();
      }
    }

    private saveDatVersion(fileData: any) {
      this.fileData.validFrom = this.validFrom?.unix();
      if (fileData.index === 0) {
        this.fileData.fileDataObu = fileData.fileData;
        this.fileData.fileNameObu = fileData.fileName;
        this.fileData.signatureObu = fileData.signature;
        this.obuFormValid = true;
      }

      if (fileData.index === 1) {
        this.fileData.fileDataDtco = fileData.fileData;
        this.fileData.fileNameDtco = fileData.fileName;
        this.fileData.signatureDtco = fileData.signature;
        this.dtcoFormValid = true;
      }
      this.fileData.uid = fileData.uid;
    }

    private close() {
      (this.$refs.formValid as HTMLFormElement).reset();
      this.dialog = false;
      this.keyObu = !this.keyObu;
      this.keyDtco = !this.keyDtco;
    }

    private confirm() {
      this.$emit('saveDatVersion', { datId: this.datId, fileData: this.fileData });
      this.close();
    }

    private onFileChange() {
      if (!this.uploaded) return;
      const reader = new FileReader();
      reader.onload = () => {
        const contents = reader.result;
        const view = new DataView(contents as ArrayBuffer);
        // First 2 bytes (UInt16, offset 0) = File ID
        this.fileData.uid = view.getUint16(0);
        // DWord starting at offset 34 (UInt32, offset 34) = File Signature
        // Convert INT to HEX for display purpose
        this.fileData.signature = view.getUint32(34).toString(16).toUpperCase();
      };
      reader.readAsArrayBuffer(this.uploaded as File);
    }

    private disabledDate(current: moment.Moment) {
      return current.isBefore(moment().startOf('day'));
    }

    private disabledTime(current: moment.Moment) {
      if (current && current.isBefore(moment().startOf('day').add(1, 'day'))) {
        if (current.hours() > moment().hours()) {
          return {
            disabledHours: () => range(0, moment().hours())
          };
        }
        if (current.minutes() === 59) {
          return {
            disabledHours: () => range(0, moment().hours() + 1)
          };
        }
        return {
          disabledHours: () => range(0, moment().hours()),
          disabledMinutes: () => range(0, moment().minutes() + 1)
        };
      }
      return {};
    }

    private isValidFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).validate() : false;
    }

    private getFile() {
      return this.$refs.fileInputValidation ? (this.$refs.fileInputValidation as HTMLFormElement).value : null;
    }

    private getCurrentUserIsAbleToWrite(featureName: string) {
      return featureName ? this.getCurrentUserIsAbleTo('write', featureName) : false;
    }
}
</script>

<style lang="scss" scoped>
@import './styles/add-dat-version.scss';
</style>
